<script setup lang="ts">
import { twMerge } from 'tailwind-merge'

import { onAuthenticated } from '~/composables/hooks'
import { useImageLightboxEvent } from '~/composables/window-events'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import { useArticle } from '~/composables/useArticle'
import { useArticleContent } from '~/composables/useArticleContent'
import { useArticleImages } from '~/composables/useArticleImages'
import { useArticleRelated } from '~/composables/useArticleRelated'
import { useIframeResize } from '~/composables/useIframeResize'

import ArticleContent from '~/components/article/ArticleContent.vue'
import ArticleCharts from '~/components/article/ArticleCharts.vue'
import ArticleForms from '~/components/article/ArticleForms.vue'
import ArticleHeader from '~/components/article/ArticleHeader.vue'
import ArticlePaywall from '~/components/article/ArticlePaywall.vue'
import ArticleRelated from '~/components/article/ArticleRelated.vue'

import { hasFlourishFullscreen } from '~/src/utils/flourish'
import { getStringOrFirstElement } from '~/utils/array'

const { $auth, $graphqlClient, $seoManager } = useNuxtApp()
const route = useRoute()
const isDesktop = useDesktop()

const slug = getStringOrFirstElement(route.params.slug)

const articleContent = ref<InstanceType<typeof ArticleContent>>()
const articleForms = ref<InstanceType<typeof ArticleForms>>()

const { article: seoInformation } = await $graphqlClient.getSeoInformation(slug)
const seo = $seoManager.generateSeoInfo(
  {
    title: seoInformation?.title,
    author: seoInformation?.memberships.authorInfo.fullName,
    publishDate: seoInformation?.date,
  },
  seoInformation?.seo,
)
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const {
  article,
  loading,
  error,
  twitterUrl,
  linkedinUrl,
  fetchArticle,
  contentHtml,
  optimizedCover,
} = useArticle(slug)

const {
  enoughArticlesForSectionOne,
  enoughArticlesForSectionTwo,
  sectionOneArticles,
  sectionTwoArticles,
} = useArticleRelated(article)

const { chartsData, formidableFormIds, hubspotFormsIds, setupContent } =
  useArticleContent()

const { setDownloadLinkOnImages } = useArticleImages()

const { setupIframeResizing } = useIframeResize()

const quote = computed(() => articleContent.value?.quote)
const content = computed(() => articleContent.value?.content)
const isFullscreen = computed(() =>
  hasFlourishFullscreen(contentHtml.value || ''),
)
const isDataVisualization = computed(() => {
  const tags = article.value?.tags?.map((tag) => tag?.slug) || []
  return tags.includes('data-visualisation')
})

const loadingChartGeneration = ref(false)
const coverPosition = ref('')

useImageLightboxEvent()

onMounted(async () => {
  await fetchArticle()
  if (error.value) return showError(error.value)

  coverPosition.value = useCoverPosition(article.value.coverUrl)
  await nextTick()

  if (isDataVisualization.value) setDownloadLinkOnImages(slug, content.value)
  setupIframeResizing(content.value)
})

watch(loadingChartGeneration, (newValue) => {
  document.body.style.overflow = newValue ? 'hidden' : 'auto'
})

watch([content, quote, article], async ([newContent, newQuote, newArticle]) => {
  if (!newContent || !newQuote || !newArticle) return

  await nextTick(async () => {
    await setupContent(newContent, newQuote)
    if (isDataVisualization.value) setDownloadLinkOnImages(slug, content.value)
    setupIframeResizing(newContent)
  })
})

onAuthenticated(async () => {
  await fetchArticle()
  if (error.value) return showError(error.value)

  coverPosition.value = useCoverPosition(article.value.coverUrl)
  await setupContent(content.value, quote.value)
})
</script>

<template>
  <div
    class="flex grow flex-col justify-center border-t border-s-300 lg:border-none"
  >
    <LoadingSpinner
      v-if="loading || $auth.store.token === undefined"
      wrap-screen
    />

    <ClientOnly v-else>
      <div
        :class="
          twMerge([
            'mx-auto w-full max-w-content-container pb-20 lg:mt-10 lg:px-5 lg:pb-36',
            isFullscreen ? 'max-w-full mx-0' : '',
          ])
        "
      >
        <div class="w-full items-start gap-x-14 lg:flex">
          <div
            :class="
              twMerge([
                'mx-auto flex w-full items-start lg:w-9/12',
                isFullscreen ? 'lg:w-full mx-0' : '',
              ])
            "
          >
            <!-- Meta (Desktop) -->
            <ArticleMetaBox
              v-if="!isFullscreen"
              class="mr-5 hidden w-52 shrink-0 lg:block"
              :categories="article.categories"
              :published-date="article.publishedDate"
              :duration="article.duration"
              :twitter-link="twitterUrl"
              :linked-in-link="linkedinUrl"
            />

            <article class="relative w-full" data-testid="main-article">
              <ArticleHeader
                :article="article"
                :cover-position="coverPosition"
                :optimized-cover="optimizedCover"
                :is-fullscreen="isFullscreen"
                :twitter-url="twitterUrl"
                :linkedin-url="linkedinUrl"
              />

              <div
                :class="
                  twMerge([
                    'mx-auto max-w-article-container px-5 lg:px-0',
                    isFullscreen ? 'max-w-full' : '',
                  ])
                "
              >
                <!-- Content -->
                <ArticleContent
                  ref="articleContent"
                  :content-html="contentHtml"
                  :is-fullscreen="isFullscreen"
                  :article="article"
                />

                <!-- Charts -->
                <ArticleCharts
                  v-if="!isFullscreen"
                  v-model:loading-chart-generation="loadingChartGeneration"
                  :charts-data="chartsData"
                />

                <!-- Forms -->
                <ArticleForms
                  v-if="!isFullscreen"
                  ref="articleForms"
                  :formidable-form-ids="formidableFormIds"
                  :hubspot-forms-ids="hubspotFormsIds"
                />

                <!-- Tags -->
                <div
                  v-if="article.viewerCanRead && !isFullscreen"
                  class="mt-6 flex flex-wrap gap-2"
                >
                  <LinkedPill
                    v-for="tag in article.tags"
                    :key="tag.id"
                    :slug="tag.slug"
                    data-testid="article-category-tag"
                  >
                    {{ tag.name }}
                  </LinkedPill>
                </div>
              </div>

              <ArticlePaywall v-if="!article.viewerCanRead" />
            </article>
          </div>

          <!-- Related content section one -->
          <ArticleRelated
            v-if="enoughArticlesForSectionOne && !isFullscreen"
            :section-one-articles="sectionOneArticles"
            section-type="one"
          />
        </div>

        <!-- Related content section two -->
        <ArticleRelated
          v-if="isDesktop && enoughArticlesForSectionTwo && !isFullscreen"
          :section-two-articles="sectionTwoArticles"
          section-type="two"
        />
      </div>
    </ClientOnly>
  </div>
</template>

<style scoped>
.chart-layout {
  @apply mb-0 max-h-[363px] w-[calc(100%_-_.2rem)] md:px-4;
}

.chart-legend-layout {
  @apply shrink-0 px-2 py-4 md:pb-8 md:pl-14 md:pr-4;
}

iframe {
  transition: height 0.3s ease-in-out;
}
</style>
